/**
 * Default Game Pass theming assets, resolved from TCM.
 *
 * TCM hosts the per-tenant brand icons on its CloudFront distribution at
 * fixed, well-known paths — the same registry the consumer apps read (see
 * trivelta-gamepass-frontend `src/lib/constants/brandIcons.ts` and the mobile
 * app's `branding_icons/index.ts`):
 *
 *   - Cash          → `common_assets/icons/primary_currency.png`
 *   - Coins         → `common_assets/icons/secondary_currency.png`
 *   - Profit Boost  → `web-app/assets/branding_icons/brand/backgrounds/img_free_profit_boost.png`
 *
 * The Challenge Complete trophy has no fixed TCM path; the consumer app
 * resolves it through the TCM assets-manifest
 * (`GET <api>/platform/v1/config/assets-manifest`, id
 * `progressEndOrangeIcon_other_icons`) and falls back to its bundled
 * `progress_end_orange_icon.png` when the manifest has no entry.
 *
 * Not every tenant has every file uploaded, so each TCM URL is verified with
 * an image preload before it is adopted — a missing file keeps the slot on its
 * fallback (bundled trophy for Challenge Complete, empty tile otherwise)
 * instead of rendering a broken image.
 *
 * Editing/persisting is a follow-up (no admin API yet) — this is read-only.
 */

import { useEffect, useState } from "react";

import { type AssetKey } from "./AssetsSection/assetsSectionData";
import { GAME_PASS_PREVIEW_IMAGES } from "./GamePassPreview/gamePassPreviewAssets";

export type TcmImageManifestEntry = {
  id: string;
  path?: string;
};

export type TcmAssetsManifest = {
  imageManifest?: TcmImageManifestEntry[];
};

/** Same public platform endpoint the consumer game pass app reads. */
export const ASSETS_MANIFEST_PATH = "/platform/v1/config/assets-manifest";

/**
 * Fixed TCM CloudFront paths per asset slot (relative to
 * `REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL`). Cash / Coins are the tenant's
 * currency symbols; Profit Boost is the promo art the mobile app renders.
 */
export const TCM_BRAND_ASSET_PATHS: Partial<Record<AssetKey, string>> = {
  cash: "common_assets/icons/primary_currency.png",
  coins: "common_assets/icons/secondary_currency.png",
  profitBoost:
    "web-app/assets/branding_icons/brand/backgrounds/img_free_profit_boost.png",
};

/**
 * Assets-manifest ids per slot, for assets themed through the manifest rather
 * than a fixed path. `progressEndOrangeIcon_other_icons` is the id
 * trivelta-gamepass-frontend resolves for the orange progress-end trophy.
 */
export const TCM_ASSET_MANIFEST_IDS: Partial<Record<AssetKey, string>> = {
  challengeComplete: "progressEndOrangeIcon_other_icons",
};

/**
 * Bundled fallbacks shown while the TCM lookups settle (and kept when a
 * tenant hasn't uploaded the file). Only the trophy has a bundled copy — the
 * currency / promo art is tenant branding, so those slots stay empty until
 * TCM serves them.
 */
export const GAME_PASS_BUNDLED_DEFAULT_ASSETS: Partial<
  Record<AssetKey, string>
> = {
  challengeComplete: GAME_PASS_PREVIEW_IMAGES.trophy,
  seasonConcluded: GAME_PASS_PREVIEW_IMAGES.seasonConcluded,
};

const getApiBaseUrl = (): string =>
  (process.env.REACT_APP_ADMIN_PANEL_BACKEND_BASE_URL ?? "")
    .trim()
    .replace(/\/+$/, "");

const getCloudFrontBaseUrl = (): string =>
  (process.env.REACT_APP_ADMIN_PANEL_CLOUDFRONT_URL ?? "")
    .trim()
    .replace(/\/+$/, "");

const toCdnUrl = (cdnBase: string, path: string): string =>
  `${cdnBase}/${path.replace(/^\/+/, "")}`;

/**
 * Appends a per-page-load cache-buster so the browser re-fetches the icons
 * after an admin replaces them in TCM — the files keep the same name
 * (`secondary_currency.png`, …), so without this the browser/CDN cache keeps
 * serving the old image.
 */
export const withCacheBuster = (url: string, buster: number): string =>
  `${url}${url.includes("?") ? "&" : "?"}v=${buster}`;

/**
 * Fetches the TCM assets-manifest. Returns `null` (rather than throwing) when
 * the env is not configured or the request fails, so callers can keep the
 * fallbacks — the same fail-soft behaviour the consumer app has.
 */
export const fetchTcmAssetsManifest =
  async (): Promise<TcmAssetsManifest | null> => {
    const baseUrl = getApiBaseUrl();
    if (!baseUrl) return null;

    try {
      // `no-store` matches the consumer app: the manifest must reflect TCM
      // edits immediately, never a cached copy.
      //
      // No credentials on purpose: `/platform/v1/config/assets-manifest` is
      // the public consumer endpoint (trivelta-gamepass-frontend fetches it
      // with a bare `fetch`, no auth), verified to respond 200 without
      // credentials on dev (test03), staging and production hosts.
      const response = await fetch(`${baseUrl}${ASSETS_MANIFEST_PATH}`, {
        cache: "no-store",
      });
      if (!response.ok) return null;
      const json = await response.json();
      // The platform API wraps the manifest in a `{ data }` envelope.
      return (json?.data ?? null) as TcmAssetsManifest | null;
    } catch (error) {
      console.warn("Failed to load TCM assets manifest", error);
      return null;
    }
  };

/**
 * Candidate TCM URL per asset slot, before existence is verified: the fixed
 * brand-icon path, plus any assets-manifest override for the manifest-driven
 * slots. Returns an empty map when the CDN base isn't configured.
 *
 * Every URL carries the shared `buster` (`?v=<timestamp>`): the preload probe
 * and the rendered `<img>` then hit the exact same URL, so the icon is
 * downloaded once and always fresh after a TCM replacement.
 *
 * Callers comparing or reusing the returned URLs across calls must pass an
 * explicit, stable `buster` — two calls relying on the `Date.now()` default
 * produce different URLs for the same asset.
 */
export const buildTcmCandidateUrls = (
  manifest: TcmAssetsManifest | null,
  buster: number = Date.now(),
): Partial<Record<AssetKey, string>> => {
  const cdnBase = getCloudFrontBaseUrl();
  if (!cdnBase) return {};

  const candidates: Partial<Record<AssetKey, string>> = {};

  (Object.keys(TCM_BRAND_ASSET_PATHS) as AssetKey[]).forEach((key) => {
    candidates[key] = withCacheBuster(
      toCdnUrl(cdnBase, TCM_BRAND_ASSET_PATHS[key] as string),
      buster,
    );
  });

  const entries = manifest?.imageManifest;
  if (Array.isArray(entries)) {
    (Object.keys(TCM_ASSET_MANIFEST_IDS) as AssetKey[]).forEach((key) => {
      const manifestId = TCM_ASSET_MANIFEST_IDS[key];
      const path = entries.find((entry) => entry?.id === manifestId)?.path;
      if (path) candidates[key] = withCacheBuster(toCdnUrl(cdnBase, path), buster);
    });
  }

  return candidates;
};

/**
 * Resolves once the browser has loaded (or failed to load) the image, so a
 * TCM URL is only adopted when the file actually exists on the CDN — some
 * tenants haven't uploaded every brand asset yet.
 *
 * Raced against a timeout: if neither `onload` nor `onerror` ever fires
 * (odd CORS failure modes, extension interference), the probe resolves
 * `false` instead of leaving `resolveTcmDefaultAssetUrls` hanging forever.
 */
export const verifyImageLoads = (
  url: string,
  timeoutMs = 8000,
): Promise<boolean> =>
  new Promise((resolve) => {
    const probe = new Image();
    const timer = setTimeout(() => resolve(false), timeoutMs);
    const done = (result: boolean) => {
      clearTimeout(timer);
      resolve(result);
    };
    probe.onload = () => done(true);
    probe.onerror = () => done(false);
    probe.src = url;
  });

/**
 * Fetches the manifest, builds the candidate TCM URLs and keeps only the ones
 * that load, merged over the bundled fallbacks.
 */
export const resolveTcmDefaultAssetUrls = async (): Promise<
  Partial<Record<AssetKey, string>>
> => {
  const manifest = await fetchTcmAssetsManifest();
  const candidates = buildTcmCandidateUrls(manifest);

  const keys = Object.keys(candidates) as AssetKey[];
  const loaded = await Promise.all(
    keys.map((key) => verifyImageLoads(candidates[key] as string)),
  );

  const urls: Partial<Record<AssetKey, string>> = {
    ...GAME_PASS_BUNDLED_DEFAULT_ASSETS,
  };
  keys.forEach((key, index) => {
    if (loaded[index]) urls[key] = candidates[key];
  });

  return urls;
};

/**
 * Default asset URLs for the Style page: starts from the bundled fallbacks so
 * the page renders immediately, then swaps in the verified TCM URLs once the
 * lookups settle.
 */
export const useGamePassDefaultAssetUrls = (): Partial<
  Record<AssetKey, string>
> => {
  const [urls, setUrls] = useState<Partial<Record<AssetKey, string>>>(
    () => ({ ...GAME_PASS_BUNDLED_DEFAULT_ASSETS }),
  );

  useEffect(() => {
    let cancelled = false;
    resolveTcmDefaultAssetUrls().then((resolved) => {
      if (!cancelled) setUrls(resolved);
    });
    return () => {
      cancelled = true;
    };
  }, []);

  return urls;
};
